import { useState, useEffect, useCallback, useRef } from 'react';
import {
  BookOpen,
  Search,
  ChevronDown,
  ChevronRight,
  Plus,
  Link2,
  Unlink,
  AlertCircle,
  Loader2,
  Sparkles,
  Upload,
  File,
  FileSpreadsheet,
  X,
  Download,
  RefreshCw,
} from 'lucide-react';
import clsx from 'clsx';
import { AccountType, type GroupAccount, type EntityAccount, type Entity } from '@/types';
import { accountsApi, entitiesApi, aiSuggestionsApi } from '@/api/client';
import { useAppStore } from '@/store';

// --- Account Type Config -----------------------------------------------------

const accountTypeConfig: Record<AccountType, { label: string; color: string; bg: string }> = {
  [AccountType.ASSET]: { label: 'Assets', color: 'text-blue-700', bg: 'bg-blue-50' },
  [AccountType.LIABILITY]: { label: 'Liabilities', color: 'text-red-700', bg: 'bg-red-50' },
  [AccountType.EQUITY]: { label: 'Equity', color: 'text-purple-700', bg: 'bg-purple-50' },
  [AccountType.REVENUE]: { label: 'Revenue', color: 'text-green-700', bg: 'bg-green-50' },
  [AccountType.EXPENSE]: { label: 'Expenses', color: 'text-amber-700', bg: 'bg-amber-50' },
};

// --- Group Account Tree Node -------------------------------------------------

function GroupAccountNode({ account, allAccounts, level = 0, forceExpand = false }: { account: GroupAccount; allAccounts: GroupAccount[]; level?: number; forceExpand?: boolean }) {
  const [expanded, setExpanded] = useState(level < 2);
  const isExpanded = forceExpand || expanded;
  const children = allAccounts.filter((a) => a.parent_id === account.id);
  const hasChildren = children.length > 0;

  return (
    <div>
      <div
        className={clsx(
          'flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-100 cursor-pointer transition-colors text-sm',
          account.is_header && 'font-semibold',
          account.is_icp_account && 'bg-primary-50/50',
          account.is_mismatch_account && 'bg-amber-50/70',
        )}
        style={{ paddingLeft: `${level * 20 + 8}px` }}
        onClick={() => hasChildren && setExpanded(!expanded)}
      >
        {hasChildren ? (
          isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
        ) : (
          <div className="w-4 flex-shrink-0" />
        )}
        <span className="font-mono text-xs text-gray-500 min-w-[90px] flex-shrink-0">{account.code}</span>
        <span className={clsx('truncate flex-1', account.is_header ? 'text-gray-900' : 'text-gray-700')}>
          {account.name}
        </span>
        {account.account_type && (
          <span className={clsx(
            'text-[10px] px-1.5 py-0.5 rounded-full flex-shrink-0 ml-auto',
            accountTypeConfig[account.account_type]?.bg ?? 'bg-gray-100',
            accountTypeConfig[account.account_type]?.color ?? 'text-gray-500',
          )}>
            {accountTypeConfig[account.account_type]?.label ?? account.account_type}
          </span>
        )}
      </div>
      {isExpanded && hasChildren && (
        <div>
          {children.map((child) => (
            <GroupAccountNode key={child.id} account={child} allAccounts={allAccounts} level={level + 1} forceExpand={forceExpand} />
          ))}
        </div>
      )}
    </div>
  );
}

// --- Chart of Accounts Page --------------------------------------------------

export default function ChartOfAccounts() {
  const [groupAccounts, setGroupAccounts] = useState<GroupAccount[]>([]);
  const [entityAccounts, setEntityAccounts] = useState<EntityAccount[]>([]);
  const [entities, setEntities] = useState<Entity[]>([]);
  const [selectedEntity, setSelectedEntity] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [entityAccountsLoading, setEntityAccountsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [groupSearch, setGroupSearch] = useState('');
  const [entitySearch, setEntitySearch] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [mappingFilter, setMappingFilter] = useState<string>('all');
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [newAccount, setNewAccount] = useState({
    code: '',
    name: '',
    account_type: AccountType.ASSET,
    parent_id: '',
    is_icp_account: false,
    is_mismatch_account: false,
    mismatch_account_id: '' as string | null,
  });
  const [savingNewAccount, setSavingNewAccount] = useState(false);

  // Upload mapping state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<{ rows_processed: number; mapped: number; skipped: number; warnings: string[]; errors: string[] } | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Upload entity COA state
  const [showCOAUpload, setShowCOAUpload] = useState(false);
  const [coaFile, setCoaFile] = useState<File | null>(null);
  const [coaUploading, setCoaUploading] = useState(false);
  const [coaUploadResult, setCoaUploadResult] = useState<{ rows_processed: number; created: number; updated: number; skipped: number; warnings: string[]; errors: string[] } | null>(null);
  const [coaDragOver, setCoaDragOver] = useState(false);
  const coaFileInputRef = useRef<HTMLInputElement>(null);

  // Upload group COA state
  const [showGroupUpload, setShowGroupUpload] = useState(false);
  const [groupFile, setGroupFile] = useState<File | null>(null);
  const [groupUploading, setGroupUploading] = useState(false);
  const [groupUploadResult, setGroupUploadResult] = useState<{ rows_processed: number; created: number; updated: number; skipped: number; warnings: string[]; errors: string[] } | null>(null);
  const [groupDragOver, setGroupDragOver] = useState(false);
  const groupFileInputRef = useRef<HTMLInputElement>(null);

  // Copy from Group COA state
  const [showCopyFromGroup, setShowCopyFromGroup] = useState(false);
  const [copyingFromGroup, setCopyingFromGroup] = useState(false);

  // AI suggestion state (for upload errors)
  const [mappingAiSuggestion, setMappingAiSuggestion] = useState<string | null>(null);
  const [mappingAiLoading, setMappingAiLoading] = useState(false);
  const [coaAiSuggestion, setCoaAiSuggestion] = useState<string | null>(null);
  const [coaAiLoading, setCoaAiLoading] = useState(false);

  const { addNotification } = useAppStore();

  // Load group accounts and entities
  const loadInitialData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [groupRes, entitiesRes] = await Promise.all([
        accountsApi.listGroup(),
        entitiesApi.list({ page: 1, page_size: 200 }),
      ]);
      setGroupAccounts(groupRes);
      setEntities(entitiesRes.items);
      if (entitiesRes.items.length > 0 && !selectedEntity) {
        setSelectedEntity(entitiesRes.items[0].id);
      }
    } catch (err: unknown) {
      const apiErr = err as { detail?: unknown; message?: string };
      const detail = apiErr.detail;
      const msg = typeof detail === 'string' ? detail
        : Array.isArray(detail) ? (detail[0] as any)?.msg ?? JSON.stringify(detail)
        : apiErr.message ?? 'Failed to load data';
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  }, [selectedEntity]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  // Load entity accounts when entity changes
  const loadEntityAccounts = useCallback(async () => {
    if (!selectedEntity) {
      setEntityAccounts([]);
      return;
    }
    setEntityAccountsLoading(true);
    try {
      const res = await accountsApi.listEntity(selectedEntity);
      setEntityAccounts(res);
    } catch {
      setEntityAccounts([]);
    } finally {
      setEntityAccountsLoading(false);
    }
  }, [selectedEntity]);

  useEffect(() => {
    loadEntityAccounts();
  }, [loadEntityAccounts]);

  // Filter group accounts — include ancestors so tree can render matching children
  const isSearchingGroup = groupSearch.trim().length > 0;
  const filteredGroupAccounts = (() => {
    const typeFiltered = filterType !== 'all'
      ? groupAccounts.filter((a) => a.account_type === filterType)
      : groupAccounts;

    if (!isSearchingGroup) return typeFiltered;

    const q = groupSearch.toLowerCase();
    const matchIds = new Set(
      typeFiltered
        .filter((a) => a.name.toLowerCase().includes(q) || a.code.toLowerCase().includes(q))
        .map((a) => a.id)
    );
    // Walk up to include all ancestors of matches
    const includeIds = new Set(matchIds);
    const byId = new Map(groupAccounts.map((a) => [a.id, a]));
    for (const id of matchIds) {
      let current = byId.get(id);
      while (current?.parent_id) {
        if (includeIds.has(current.parent_id)) break;
        includeIds.add(current.parent_id);
        current = byId.get(current.parent_id);
      }
    }
    return groupAccounts.filter((a) => includeIds.has(a.id));
  })();

  const rootGroupAccounts = filteredGroupAccounts.filter((a) => a.parent_id === null || !filteredGroupAccounts.some((p) => p.id === a.parent_id));

  // Filter entity accounts
  const filteredEntityAccounts = entityAccounts.filter((a) => {
    if (mappingFilter === 'mapped' && !a.is_mapped) return false;
    if (mappingFilter === 'unmapped' && a.is_mapped) return false;
    if (entitySearch && !a.local_name.toLowerCase().includes(entitySearch.toLowerCase()) && !a.local_code.toLowerCase().includes(entitySearch.toLowerCase())) return false;
    return true;
  });

  const mappedCount = entityAccounts.filter((a) => a.is_mapped).length;

  const currentEntity = entities.find((e) => e.id === selectedEntity);

  const handleMapAccount = async (entityAccountId: string, groupAccountId: string) => {
    try {
      await accountsApi.mapAccount(entityAccountId, groupAccountId);
      addNotification({ type: 'success', title: 'Account Mapped', message: 'Entity account has been mapped to the group account.' });
      await loadEntityAccounts();
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      addNotification({ type: 'error', title: 'Mapping Failed', message: apiErr.detail || 'Failed to map account.' });
    }
  };

  const handleUnmapAccount = async (entityAccountId: string) => {
    try {
      await accountsApi.unmapAccount(entityAccountId);
      addNotification({ type: 'success', title: 'Account Unmapped', message: 'Entity account mapping has been removed.' });
      await loadEntityAccounts();
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      addNotification({ type: 'error', title: 'Unmap Failed', message: apiErr.detail || 'Failed to unmap account.' });
    }
  };

  const handleAddGroupAccount = async () => {
    setSavingNewAccount(true);
    try {
      await accountsApi.createGroup({
        code: newAccount.code,
        name: newAccount.name,
        account_type: newAccount.account_type,
        parent_id: newAccount.parent_id || null,
        is_icp_account: newAccount.is_icp_account,
        is_mismatch_account: newAccount.is_mismatch_account,
        mismatch_account_id: newAccount.mismatch_account_id || null,
        is_active: true,
      });
      addNotification({ type: 'success', title: 'Account Created', message: `Group account ${newAccount.code} created.` });
      setShowAddAccount(false);
      setNewAccount({
        code: '',
        name: '',
        account_type: AccountType.ASSET,
        parent_id: '',
        is_icp_account: false,
        is_mismatch_account: false,
        mismatch_account_id: null,
      });
      const groupRes = await accountsApi.listGroup();
      setGroupAccounts(groupRes);
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      addNotification({ type: 'error', title: 'Create Failed', message: apiErr.detail || 'Failed to create account.' });
    } finally {
      setSavingNewAccount(false);
    }
  };

  const handleUploadMapping = async () => {
    if (!uploadFile || !selectedEntity) return;
    setUploading(true);
    setUploadResult(null);
    try {
      const result = await accountsApi.uploadMapping(selectedEntity, uploadFile);
      setUploadResult(result);
      addNotification({
        type: result.errors.length > 0 ? 'warning' : 'success',
        title: 'Mapping Uploaded',
        message: `${result.mapped} accounts mapped, ${result.skipped} skipped.`,
      });
      await loadEntityAccounts();
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      const errMsg = apiErr.detail || 'Failed to upload mapping.';
      setUploadResult({ rows_processed: 0, mapped: 0, skipped: 0, warnings: [], errors: [errMsg] });
      addNotification({ type: 'error', title: 'Upload Failed', message: errMsg });
    } finally {
      setUploading(false);
    }
  };

  const handleCloseUploadModal = () => {
    setShowUploadModal(false);
    setUploadFile(null);
    setUploadResult(null);
    setMappingAiSuggestion(null);
  };

  const handleUploadCOA = async () => {
    if (!coaFile || !selectedEntity) return;
    setCoaUploading(true);
    setCoaUploadResult(null);
    try {
      const result = await accountsApi.uploadCOA(selectedEntity, coaFile);
      setCoaUploadResult(result);
      addNotification({
        type: result.errors.length > 0 ? 'warning' : 'success',
        title: 'Entity COA Uploaded',
        message: `${result.created} created, ${result.updated} updated, ${result.skipped} skipped.`,
      });
      await loadEntityAccounts();
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      const errMsg = apiErr.detail || 'Failed to upload entity COA.';
      setCoaUploadResult({ rows_processed: 0, created: 0, updated: 0, skipped: 0, warnings: [], errors: [errMsg] });
      addNotification({ type: 'error', title: 'COA Upload Failed', message: errMsg });
    } finally {
      setCoaUploading(false);
    }
  };

  const handleCloseCOAUpload = () => {
    setShowCOAUpload(false);
    setCoaFile(null);
    setCoaUploadResult(null);
    setCoaAiSuggestion(null);
  };

  // --- Group COA Upload handlers ---
  const handleUploadGroupCOA = async () => {
    if (!groupFile) return;
    setGroupUploading(true);
    setGroupUploadResult(null);
    try {
      const result = await accountsApi.uploadGroupCoa(groupFile);
      setGroupUploadResult(result);
      addNotification({
        type: result.errors.length > 0 ? 'warning' : 'success',
        title: 'Group COA Uploaded',
        message: `${result.created} created, ${result.updated} updated, ${result.skipped} skipped.`,
      });
      const groupRes = await accountsApi.listGroup();
      setGroupAccounts(groupRes);
    } catch (err: unknown) {
      const apiErr = err as { response?: { data?: { detail?: string } }; message?: string };
      const errMsg = apiErr.response?.data?.detail || 'Failed to upload group COA.';
      setGroupUploadResult({ rows_processed: 0, created: 0, updated: 0, skipped: 0, warnings: [], errors: [errMsg] });
      addNotification({ type: 'error', title: 'Group COA Upload Failed', message: errMsg });
    } finally {
      setGroupUploading(false);
    }
  };

  const handleCloseGroupUpload = () => {
    setShowGroupUpload(false);
    setGroupFile(null);
    setGroupUploadResult(null);
  };

  const handleCopyFromGroup = async () => {
    if (!selectedEntity) return;
    setCopyingFromGroup(true);
    try {
      const result = await accountsApi.copyFromGroup(selectedEntity);
      addNotification({
        type: 'success',
        title: 'Copied from Group COA',
        message: `${result.created} accounts created, ${result.skipped} skipped (${result.total_group_accounts} group accounts total).`,
      });
      setShowCopyFromGroup(false);
      await loadEntityAccounts();
    } catch (err: unknown) {
      const apiErr = err as { response?: { data?: { detail?: string } }; message?: string };
      addNotification({
        type: 'error',
        title: 'Copy Failed',
        message: apiErr.response?.data?.detail || 'Failed to copy from group COA.',
      });
    } finally {
      setCopyingFromGroup(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="skeleton w-64 h-7" />
        <div className="skeleton w-full h-16 rounded-xl" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="card p-6 animate-pulse">
            <div className="skeleton w-48 h-5 mb-4" />
            <div className="space-y-2">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="skeleton w-full h-6" />
              ))}
            </div>
          </div>
          <div className="card p-6 animate-pulse">
            <div className="skeleton w-48 h-5 mb-4" />
            <div className="space-y-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="skeleton w-full h-10" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Chart of Accounts</h1>
        </div>
        <div className="card p-8 text-center">
          <AlertCircle className="w-12 h-12 mx-auto text-red-300 mb-3" />
          <p className="text-sm text-red-600">{typeof error === 'string' ? error : JSON.stringify(error)}</p>
          <button onClick={loadInitialData} className="btn-primary mt-4">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Chart of Accounts</h1>
          <p className="text-sm text-gray-500 mt-1">Group chart of accounts and entity account mapping</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowGroupUpload(true)}
            className="btn-secondary"
            title="Upload group chart of accounts (Excel/CSV)"
          >
            <Upload className="w-4 h-4 mr-2" />
            Upload Group COA
          </button>
          {/* Hidden: Upload Entity COA & Upload Mapping buttons */}
          {false && <>
          <button
            onClick={() => setShowCOAUpload(true)}
            disabled={!selectedEntity}
            className="btn-secondary"
            title="Upload entity chart of accounts (Excel/CSV)"
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Upload Entity COA
          </button>
          <button
            onClick={() => setShowUploadModal(true)}
            disabled={!selectedEntity}
            className="btn-primary"
            title="Upload a mapping Excel file for the selected entity"
          >
            <Upload className="w-4 h-4 mr-2" />
            Upload Mapping
          </button>
          </>}
        </div>
      </div>

      {/* Entity Selector — prominent, top of page */}
      {/* Entity selector hidden */}

      {/* Single-panel layout */}
      <div className="grid grid-cols-1 gap-6">
        {/* Left Panel: Group Chart of Accounts */}
        <div className="card flex flex-col max-h-[calc(100vh-320px)]">
          <div className="p-4 border-b border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-gray-900">Group Chart of Accounts</h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={async () => {
                    try {
                      const res = await accountsApi.syncIcpFlags();
                      addNotification({ type: 'success', title: 'ICP Accounts Synced', message: res.message });
                      const groupRes = await accountsApi.listGroup();
                      setGroupAccounts(groupRes);
                    } catch (e) {
                      const err = e as { detail?: string };
                      addNotification({ type: 'error', title: 'Sync Failed', message: err.detail || 'Failed to sync ICP flags.' });
                    }
                  }}
                  className="flex items-center gap-1 text-xs font-medium text-gray-600 hover:text-gray-800"
                  title="Auto-set ICP account flags from Trial Balance or pattern match"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  Sync ICP
                </button>
                <button
                  onClick={() => setShowAddAccount(!showAddAccount)}
                  className="flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Add Account
                </button>
              </div>
            </div>

            {showAddAccount && (
              <div className="mb-3 p-3 bg-gray-50 rounded-lg border border-gray-200 space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <input
                    className="input-field text-xs"
                    placeholder="Account code"
                    value={newAccount.code}
                    onChange={(e) => setNewAccount({ ...newAccount, code: e.target.value })}
                  />
                  <select
                    className="select-field text-xs"
                    value={newAccount.account_type}
                    onChange={(e) => setNewAccount({ ...newAccount, account_type: e.target.value as AccountType })}
                  >
                    {Object.values(AccountType).map((t) => (
                      <option key={t} value={t}>{accountTypeConfig[t].label}</option>
                    ))}
                  </select>
                </div>
                <input
                  className="input-field text-xs"
                  placeholder="Account name"
                  value={newAccount.name}
                  onChange={(e) => setNewAccount({ ...newAccount, name: e.target.value })}
                />
                <div className="flex items-center gap-4 flex-wrap">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="is_icp_account"
                      checked={newAccount.is_icp_account}
                      onChange={(e) => setNewAccount({ ...newAccount, is_icp_account: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <label htmlFor="is_icp_account" className="text-xs text-gray-700">ICP Account</label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="is_mismatch_account"
                      checked={newAccount.is_mismatch_account}
                      onChange={(e) => setNewAccount({ ...newAccount, is_mismatch_account: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <label htmlFor="is_mismatch_account" className="text-xs text-gray-700">Mismatch Account</label>
                  </div>
                </div>
                {newAccount.is_icp_account && (
                  <div>
                    <label htmlFor="mismatch_account" className="block text-xs text-gray-600 mb-1">Mismatch Account (for posting unmatched IC differences)</label>
                    <select
                      id="mismatch_account"
                      className="select-field text-xs w-full"
                      value={newAccount.mismatch_account_id ?? ''}
                      onChange={(e) => setNewAccount({ ...newAccount, mismatch_account_id: e.target.value || null })}
                    >
                      <option value="">— None —</option>
                      {groupAccounts
                        .filter((a) => a.is_mismatch_account)
                        .map((g) => (
                          <option key={g.id} value={g.id}>
                            {g.code} - {g.name}
                          </option>
                        ))}
                    </select>
                  </div>
                )}
                <div className="flex justify-end gap-2">
                  <button onClick={() => setShowAddAccount(false)} className="text-xs text-gray-500 hover:text-gray-700">Cancel</button>
                  <button onClick={handleAddGroupAccount} className="btn-primary text-xs py-1 px-3" disabled={!newAccount.code || !newAccount.name || savingNewAccount}>
                    {savingNewAccount ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Add'}
                  </button>
                </div>
              </div>
            )}

            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <input
                  className="input-field text-xs pl-8"
                  placeholder="Search accounts..."
                  value={groupSearch}
                  onChange={(e) => setGroupSearch(e.target.value)}
                />
              </div>
              <select
                className="select-field text-xs w-32"
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
              >
                <option value="all">All Types</option>
                {Object.values(AccountType).map((t) => (
                  <option key={t} value={t}>{accountTypeConfig[t].label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto scrollbar-thin p-2">
            {filterType === 'all' ? (
              Object.values(AccountType).map((type) => {
                const typeAccounts = filteredGroupAccounts.filter((a) => a.account_type === type);
                const typeRoots = typeAccounts.filter((a) => a.parent_id === null || !typeAccounts.some((p) => p.id === a.parent_id));
                if (typeRoots.length === 0) return null;
                return (
                  <div key={type} className="mb-2">
                    <div className={clsx('px-2 py-1 mb-1 rounded text-xs font-bold uppercase tracking-wider', accountTypeConfig[type].bg, accountTypeConfig[type].color)}>
                      {accountTypeConfig[type].label}
                    </div>
                    {typeRoots.map((account) => (
                      <GroupAccountNode key={account.id} account={account} allAccounts={filteredGroupAccounts} forceExpand={isSearchingGroup} />
                    ))}
                  </div>
                );
              })
            ) : (
              rootGroupAccounts.map((account) => (
                <GroupAccountNode key={account.id} account={account} allAccounts={filteredGroupAccounts} forceExpand={isSearchingGroup} />
              ))
            )}
            {groupAccounts.length === 0 && (
              <div className="p-8 text-center text-gray-400 text-sm">
                No group accounts found
              </div>
            )}
            {groupAccounts.length > 0 && filteredGroupAccounts.length === 0 && (
              <div className="p-8 text-center text-gray-400 text-sm">
                No accounts match &ldquo;{groupSearch}&rdquo;
              </div>
            )}
          </div>

          <div className="p-3 border-t border-gray-100 bg-gray-50 text-xs text-gray-500">
            {groupAccounts.length} accounts &middot; {groupAccounts.filter((a) => a.is_header).length} headers
          </div>
        </div>

        {/* Right Panel: Entity Account Mapping — hidden */}
        {false && <div className="card flex flex-col max-h-[calc(100vh-320px)]">
          <div className="p-4 border-b border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-gray-900">
                {currentEntity ? `${currentEntity?.name} — Account Mapping` : 'Entity Account Mapping'}
              </h2>
              {selectedEntity && groupAccounts.length > 0 && (
                <button
                  onClick={() => setShowCopyFromGroup(true)}
                  className="flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700"
                  title="Copy all group accounts as entity accounts (pre-mapped)"
                >
                  <BookOpen className="w-3.5 h-3.5" />
                  Copy from Group
                </button>
              )}
            </div>

            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <input
                  className="input-field text-xs pl-8"
                  placeholder="Search entity accounts..."
                  value={entitySearch}
                  onChange={(e) => setEntitySearch(e.target.value)}
                />
              </div>
              <select
                className="select-field text-xs w-32"
                value={mappingFilter}
                onChange={(e) => setMappingFilter(e.target.value)}
              >
                <option value="all">All</option>
                <option value="mapped">Mapped</option>
                <option value="unmapped">Unmapped</option>
              </select>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto scrollbar-thin">
            {entityAccountsLoading ? (
              <div className="p-6 space-y-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="skeleton w-full h-14" />
                ))}
              </div>
            ) : !selectedEntity ? (
              <div className="p-8 text-center">
                <BookOpen className="w-10 h-10 mx-auto text-gray-300 mb-2" />
                <p className="text-sm text-gray-500">Select an entity above to view its accounts</p>
              </div>
            ) : filteredEntityAccounts.length === 0 ? (
              <div className="p-8 text-center">
                <BookOpen className="w-10 h-10 mx-auto text-gray-300 mb-2" />
                <p className="text-sm text-gray-500">No entity accounts found</p>
                <p className="text-xs text-gray-400 mt-1">Upload an entity COA, mapping file, or trial balance to populate accounts</p>
                <div className="flex items-center justify-center gap-2 mt-3 flex-wrap">
                  <button onClick={() => setShowCOAUpload(true)} className="btn-secondary text-xs">
                    <FileSpreadsheet className="w-3.5 h-3.5 mr-1" />
                    Upload Entity COA
                  </button>
                  <button onClick={() => setShowUploadModal(true)} className="btn-primary text-xs">
                    <Upload className="w-3.5 h-3.5 mr-1" />
                    Upload Mapping
                  </button>
                  {groupAccounts.length > 0 && (
                    <button onClick={() => setShowCopyFromGroup(true)} className="btn-secondary text-xs">
                      <BookOpen className="w-3.5 h-3.5 mr-1" />
                      Copy from Group
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="divide-y divide-gray-50">
                {filteredEntityAccounts.map((account) => (
                  <div key={account.id} className="px-4 py-3 hover:bg-gray-50/50 transition-colors">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs text-gray-500">{account.local_code}</span>
                          {account.account_type && accountTypeConfig[account.account_type as AccountType] && (
                            <span className={clsx('badge text-[10px]', accountTypeConfig[account.account_type as AccountType].bg, accountTypeConfig[account.account_type as AccountType].color)}>
                              {accountTypeConfig[account.account_type as AccountType].label}
                            </span>
                          )}
                        </div>
                        <p className="text-sm font-medium text-gray-800 mt-0.5">{account.local_name}</p>
                      </div>
                      {account.is_mapped ? (
                        <button
                          onClick={() => handleUnmapAccount(account.id)}
                          className="flex-shrink-0 p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                          title="Unmap account"
                        >
                          <Unlink className="w-3.5 h-3.5" />
                        </button>
                      ) : (
                        <span className="flex-shrink-0">
                          <Sparkles className="w-3.5 h-3.5 text-amber-400" />
                        </span>
                      )}
                    </div>

                    {account.is_mapped ? (
                      <div className="flex items-center gap-2 px-2 py-1.5 bg-green-50 rounded border border-green-100">
                        <Link2 className="w-3 h-3 text-green-600 flex-shrink-0" />
                        <span className="text-xs text-green-700 font-mono">{account.group_account_code}</span>
                        <span className="text-xs text-green-800">{account.group_account_name}</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <select
                          className="select-field text-xs flex-1"
                          defaultValue=""
                          onChange={(e) => {
                            if (e.target.value) handleMapAccount(account.id, e.target.value);
                          }}
                        >
                          <option value="" disabled>Select group account to map...</option>
                          {groupAccounts
                            .filter((g) => !g.is_header && g.account_type === account.account_type)
                            .map((g) => (
                              <option key={g.id} value={g.id}>
                                {g.code} - {g.name}
                              </option>
                            ))}
                        </select>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="p-3 border-t border-gray-100 bg-gray-50">
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">
                {filteredEntityAccounts.length} of {entityAccounts.length} accounts
              </span>
              {entityAccounts.length > 0 && (
                <div className="flex items-center gap-2">
                  <div className="w-32 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-green-500 rounded-full transition-all duration-300"
                      style={{ width: `${Math.round((mappedCount / entityAccounts.length) * 100)}%` }}
                    />
                  </div>
                  <span className="text-xs font-medium text-gray-700">
                    {Math.round((mappedCount / entityAccounts.length) * 100)}% mapped
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>}
      </div>

      {/* Upload Mapping Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Upload Account Mapping</h2>
                {currentEntity && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    Entity: <span className="font-medium text-gray-700">{currentEntity.name}</span>
                    <span className="font-mono ml-1 text-gray-500">({currentEntity.code})</span>
                  </p>
                )}
              </div>
              <button onClick={handleCloseUploadModal} className="p-1 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Upload result */}
              {uploadResult && (
                <div className={clsx(
                  'px-4 py-3 rounded-lg border space-y-1',
                  uploadResult.errors.length > 0 ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200',
                )}>
                  <p className={clsx('text-sm font-medium', uploadResult.errors.length > 0 ? 'text-amber-800' : 'text-green-800')}>
                    {uploadResult.mapped} accounts mapped · {uploadResult.skipped} skipped · {uploadResult.rows_processed} rows processed
                  </p>
                  {uploadResult.warnings.slice(0, 5).map((w, i) => (
                    <p key={i} className="text-xs text-amber-700">&bull; {w}</p>
                  ))}
                  {uploadResult.warnings.length > 5 && (
                    <p className="text-xs text-amber-600">…and {uploadResult.warnings.length - 5} more warnings</p>
                  )}
                  {uploadResult.errors.length > 0 && (
                    <p className="text-xs text-red-700 mt-1">{uploadResult.errors.join('; ')}</p>
                  )}
                </div>
              )}

              {/* AI Suggestion for mapping errors */}
              {uploadResult && uploadResult.errors.length > 0 && (
                <div className="space-y-2">
                  {!mappingAiSuggestion ? (
                    <button
                      onClick={async () => {
                        setMappingAiLoading(true);
                        try {
                          const res = await aiSuggestionsApi.getUploadSuggestion({
                            upload_type: 'mapping',
                            error_message: uploadResult.errors.join('; '),
                            warnings: uploadResult.warnings,
                            file_name: uploadFile?.name || undefined,
                          });
                          setMappingAiSuggestion(res.suggestion);
                        } catch {
                          setMappingAiSuggestion('AI suggestion unavailable. Configure AI in Settings → AI / LLM.');
                        } finally {
                          setMappingAiLoading(false);
                        }
                      }}
                      disabled={mappingAiLoading}
                      className="flex items-center gap-2 text-sm text-purple-600 hover:text-purple-700 font-medium transition-colors"
                    >
                      {mappingAiLoading ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> Analyzing with AI...</>
                      ) : (
                        <><Sparkles className="w-4 h-4" /> Get AI Suggestion</>
                      )}
                    </button>
                  ) : (
                    <div className="px-4 py-3 rounded-lg border border-purple-200 bg-purple-50">
                      <div className="flex items-start gap-2">
                        <Sparkles className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-xs font-medium text-purple-800 mb-1">AI Suggestion</p>
                          <p className="text-sm text-purple-700">{mappingAiSuggestion}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Format note */}
              <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg border border-blue-100">
                <Download className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-blue-700">
                  <p className="font-medium">Expected format: Mapping Excel or CSV</p>
                  <p className="mt-0.5">Excel: requires an <span className="font-mono">Account</span> sheet with columns <span className="font-mono">Source</span> and <span className="font-mono">Target</span>. CSV: columns <span className="font-mono">Source</span> and <span className="font-mono">Target</span> (group account code, e.g. <span className="font-mono">AC_1207002001</span>).</p>
                </div>
              </div>

              {/* Drop zone */}
              <div
                className={clsx(
                  'border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer',
                  dragOver ? 'border-primary-400 bg-primary-50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50',
                )}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files?.[0]; if (f) setUploadFile(f); }}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) setUploadFile(f); }}
                />
                {uploadFile ? (
                  <div className="flex items-center justify-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
                      <File className="w-5 h-5 text-green-600" />
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-medium text-gray-900">{uploadFile.name}</p>
                      <p className="text-xs text-gray-500">{(uploadFile.size / 1024).toFixed(1)} KB</p>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); setUploadFile(null); }}
                      className="p-1 hover:bg-gray-100 rounded"
                    >
                      <X className="w-4 h-4 text-gray-400" />
                    </button>
                  </div>
                ) : (
                  <>
                    <Upload className="w-8 h-8 text-gray-400 mx-auto mb-3" />
                    <p className="text-sm text-gray-600 mb-1">
                      Drag and drop your mapping file, or <span className="text-primary-600 font-medium">browse</span>
                    </p>
                    <p className="text-xs text-gray-400">Supports Excel (.xlsx, .xls) and CSV (.csv)</p>
                  </>
                )}
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
              <button onClick={handleCloseUploadModal} className="btn-secondary">Close</button>
              <button onClick={handleUploadMapping} disabled={uploading || !uploadFile} className="btn-primary">
                {uploading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading...</>
                ) : (
                  <><Upload className="w-4 h-4 mr-2" />Upload & Map</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Group COA Modal */}
      {showGroupUpload && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Upload Group Chart of Accounts</h2>
                <p className="text-xs text-gray-500 mt-0.5">Upload your group-level COA structure (Excel or CSV)</p>
              </div>
              <button onClick={handleCloseGroupUpload} className="p-1 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {groupUploadResult && (
                <div className={clsx(
                  'px-4 py-3 rounded-lg border space-y-1',
                  groupUploadResult.errors.length > 0 ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200',
                )}>
                  <p className={clsx('text-sm font-medium', groupUploadResult.errors.length > 0 ? 'text-amber-800' : 'text-green-800')}>
                    {groupUploadResult.created} created &middot; {groupUploadResult.updated} updated &middot; {groupUploadResult.skipped} skipped &middot; {groupUploadResult.rows_processed} rows processed
                  </p>
                  {groupUploadResult.warnings.slice(0, 5).map((w, i) => (
                    <p key={i} className="text-xs text-amber-700">&bull; {w}</p>
                  ))}
                  {groupUploadResult.warnings.length > 5 && (
                    <p className="text-xs text-amber-600">&hellip;and {groupUploadResult.warnings.length - 5} more warnings</p>
                  )}
                  {groupUploadResult.errors.length > 0 && (
                    <div className="mt-1 space-y-0.5">
                      {groupUploadResult.errors.slice(0, 5).map((e, i) => (
                        <p key={i} className="text-xs text-red-700">&bull; {e}</p>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg border border-blue-100">
                <Download className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-blue-700">
                  <p className="font-medium">Expected format: Group COA (Excel or CSV)</p>
                  <p className="mt-0.5">
                    Required: <span className="font-mono">code</span>, <span className="font-mono">name</span>, <span className="font-mono">account_type</span> (asset/liability/equity/revenue/expense)
                  </p>
                  <p className="mt-0.5">
                    Optional: <span className="font-mono">fs_line_item</span>, <span className="font-mono">is_balance_sheet</span>, <span className="font-mono">parent_code</span>, <span className="font-mono">sort_order</span>
                  </p>
                  <button
                    onClick={() => accountsApi.downloadGroupCoaTemplate()}
                    className="mt-1.5 text-blue-600 hover:text-blue-800 font-medium underline"
                  >
                    Download template CSV
                  </button>
                </div>
              </div>

              <div
                className={clsx(
                  'border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer',
                  groupDragOver ? 'border-primary-400 bg-primary-50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50',
                )}
                onDragOver={(e) => { e.preventDefault(); setGroupDragOver(true); }}
                onDragLeave={() => setGroupDragOver(false)}
                onDrop={(e) => { e.preventDefault(); setGroupDragOver(false); const f = e.dataTransfer.files?.[0]; if (f) setGroupFile(f); }}
                onClick={() => groupFileInputRef.current?.click()}
              >
                <input
                  ref={groupFileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) setGroupFile(f); }}
                />
                {groupFile ? (
                  <div className="flex items-center justify-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
                      <File className="w-5 h-5 text-green-600" />
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-medium text-gray-900">{groupFile.name}</p>
                      <p className="text-xs text-gray-500">{(groupFile.size / 1024).toFixed(1)} KB</p>
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); setGroupFile(null); }} className="p-1 hover:bg-gray-100 rounded">
                      <X className="w-4 h-4 text-gray-400" />
                    </button>
                  </div>
                ) : (
                  <>
                    <Upload className="w-8 h-8 text-gray-400 mx-auto mb-3" />
                    <p className="text-sm text-gray-600 mb-1">
                      Drag and drop your Group COA file, or <span className="text-primary-600 font-medium">browse</span>
                    </p>
                    <p className="text-xs text-gray-400">Supports Excel (.xlsx, .xls) and CSV (.csv)</p>
                  </>
                )}
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
              <button onClick={handleCloseGroupUpload} className="btn-secondary">Close</button>
              <button onClick={handleUploadGroupCOA} disabled={groupUploading || !groupFile} className="btn-primary">
                {groupUploading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading...</>
                ) : (
                  <><Upload className="w-4 h-4 mr-2" />Upload Group COA</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Entity COA Modal */}
      {showCOAUpload && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Upload Entity Chart of Accounts</h2>
                {selectedEntity && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    Entity: {entities.find((e) => e.id === selectedEntity)?.name}{' '}
                    <span className="font-mono text-gray-400">({entities.find((e) => e.id === selectedEntity)?.code})</span>
                  </p>
                )}
              </div>
              <button onClick={handleCloseCOAUpload} className="p-1 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {coaUploadResult && (
                <div className={clsx(
                  'px-4 py-3 rounded-lg border space-y-1',
                  coaUploadResult.errors.length > 0 ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200',
                )}>
                  <p className={clsx('text-sm font-medium', coaUploadResult.errors.length > 0 ? 'text-amber-800' : 'text-green-800')}>
                    {coaUploadResult.created} created · {coaUploadResult.updated} updated · {coaUploadResult.skipped} skipped · {coaUploadResult.rows_processed} rows processed
                  </p>
                  {coaUploadResult.warnings.slice(0, 5).map((w, i) => (
                    <p key={i} className="text-xs text-amber-700">&bull; {w}</p>
                  ))}
                  {coaUploadResult.warnings.length > 5 && (
                    <p className="text-xs text-amber-600">…and {coaUploadResult.warnings.length - 5} more warnings</p>
                  )}
                  {coaUploadResult.errors.length > 0 && (
                    <p className="text-xs text-red-700 mt-1">{coaUploadResult.errors.join('; ')}</p>
                  )}
                </div>
              )}

              {/* AI Suggestion for COA errors */}
              {coaUploadResult && coaUploadResult.errors.length > 0 && (
                <div className="space-y-2">
                  {!coaAiSuggestion ? (
                    <button
                      onClick={async () => {
                        setCoaAiLoading(true);
                        try {
                          const res = await aiSuggestionsApi.getUploadSuggestion({
                            upload_type: 'coa',
                            error_message: coaUploadResult.errors.join('; '),
                            warnings: coaUploadResult.warnings,
                            file_name: coaFile?.name || undefined,
                          });
                          setCoaAiSuggestion(res.suggestion);
                        } catch {
                          setCoaAiSuggestion('AI suggestion unavailable. Configure AI in Settings → AI / LLM.');
                        } finally {
                          setCoaAiLoading(false);
                        }
                      }}
                      disabled={coaAiLoading}
                      className="flex items-center gap-2 text-sm text-purple-600 hover:text-purple-700 font-medium transition-colors"
                    >
                      {coaAiLoading ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> Analyzing with AI...</>
                      ) : (
                        <><Sparkles className="w-4 h-4" /> Get AI Suggestion</>
                      )}
                    </button>
                  ) : (
                    <div className="px-4 py-3 rounded-lg border border-purple-200 bg-purple-50">
                      <div className="flex items-start gap-2">
                        <Sparkles className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-xs font-medium text-purple-800 mb-1">AI Suggestion</p>
                          <p className="text-sm text-purple-700">{coaAiSuggestion}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg border border-blue-100">
                <Download className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-blue-700">
                  <p className="font-medium">Expected format: Entity COA (Excel or CSV)</p>
                  <p className="mt-0.5">Columns: <span className="font-mono">account_code</span> (required) and <span className="font-mono">account_name</span> (required).</p>
                </div>
              </div>

              <div
                className={clsx(
                  'border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer',
                  coaDragOver ? 'border-primary-400 bg-primary-50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50',
                )}
                onDragOver={(e) => { e.preventDefault(); setCoaDragOver(true); }}
                onDragLeave={() => setCoaDragOver(false)}
                onDrop={(e) => { e.preventDefault(); setCoaDragOver(false); const f = e.dataTransfer.files?.[0]; if (f) setCoaFile(f); }}
                onClick={() => coaFileInputRef.current?.click()}
              >
                <input
                  ref={coaFileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) setCoaFile(f); }}
                />
                {coaFile ? (
                  <div className="flex items-center justify-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
                      <File className="w-5 h-5 text-green-600" />
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-medium text-gray-900">{coaFile.name}</p>
                      <p className="text-xs text-gray-500">{(coaFile.size / 1024).toFixed(1)} KB</p>
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); setCoaFile(null); }} className="p-1 hover:bg-gray-100 rounded">
                      <X className="w-4 h-4 text-gray-400" />
                    </button>
                  </div>
                ) : (
                  <>
                    <Upload className="w-8 h-8 text-gray-400 mx-auto mb-3" />
                    <p className="text-sm text-gray-600 mb-1">
                      Drag and drop your COA file, or <span className="text-primary-600 font-medium">browse</span>
                    </p>
                    <p className="text-xs text-gray-400">Supports Excel (.xlsx, .xls) and CSV (.csv)</p>
                  </>
                )}
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
              <button onClick={handleCloseCOAUpload} className="btn-secondary">Close</button>
              <button onClick={handleUploadCOA} disabled={coaUploading || !coaFile} className="btn-primary">
                {coaUploading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading...</>
                ) : (
                  <><FileSpreadsheet className="w-4 h-4 mr-2" />Upload COA</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Copy from Group Confirmation Dialog */}
      {showCopyFromGroup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">Copy from Group COA</h2>
              <button onClick={() => setShowCopyFromGroup(false)} className="p-1 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            <div className="p-6 space-y-3">
              <p className="text-sm text-gray-700">
                This will copy all <span className="font-semibold">{groupAccounts.length}</span> group accounts as entity accounts for{' '}
                <span className="font-semibold">{currentEntity?.name}</span>, pre-mapped one-to-one.
              </p>
              <p className="text-xs text-gray-500">
                Accounts that already exist (by code) will be skipped.
              </p>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
              <button onClick={() => setShowCopyFromGroup(false)} className="btn-secondary">Cancel</button>
              <button onClick={handleCopyFromGroup} disabled={copyingFromGroup} className="btn-primary">
                {copyingFromGroup ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Copying...</>
                ) : (
                  <><BookOpen className="w-4 h-4 mr-2" />Copy Accounts</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
